import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {Asset} from '../../../model/asset';
import {AssetsService} from '../../../services/assets.service';
import {MeasuresService} from '../../../services/measures.service';
import {MapMeasurement} from '../../../model/custom/map/map-measurement';
import {translate} from '@ngneat/transloco';
import {TimezoneService} from '../../../services/timezone.service';
import {MapAsset} from '../../../model/custom/map/map-asset';
import {MapIconsService} from '../../../services/map-icons.service';
import {AssetRepresentationsService} from '../../../services/asset-representations.service';
import {Subscription} from 'rxjs';
import {MessageService} from 'primeng/api';

type TimeOption = { label: string, startDate: Date };
type TriggerSummaryDetail = {
  name: string,
  entityType: string,
  expression: string,
  type: string,
  assetUuid?: string,
  deviceUuid?: string,
  assetMeasurementName?: string,
  deviceMeasurementName?: string,
  lastValueEvaluated: string,
  category: string
};
type TriggersByStatus = {
  alarmTriggers: TriggerSummaryDetail[],
  warningTriggers: TriggerSummaryDetail[],
  inactivityTriggers: TriggerSummaryDetail[]
};

@Component({
  selector: 'app-map-asset-details',
  templateUrl: './map-asset-details.component.html',
  styleUrls: ['./map-asset-details.component.scss']
})
export class MapAssetDetailsComponent {

  @Input() public hashedMapAsset: Map<string, MapAsset>;
  @Output() public assetChangedStatus: EventEmitter<string>;

  public asset: Asset;

  public measurements: MapMeasurement[] = [];
  public selectedMeasurement: MapMeasurement;

  public chartMeasures: any[] = [];
  public rangeDates: Date[];
  public graphAxis: any;

  public timeOptions: TimeOption[];

  public triggersByStatus: TriggersByStatus;
  public triggersCarousel: { status: string, count: number }[];

  public status?: string;
  public statusColor?: string;

  public selectedStatus: any;
  public selectedStatusCategory: string;

  public loadedAsset = false;
  public loadedAssetMeasurements = false;
  public loadedAssetDeviceMeasurements = false;
  public loadedAssetTriggers = false;

  public observerTriggers: Subscription;

  constructor(private assetsService: AssetsService,
              private timezoneService: TimezoneService,
              private measuresService: MeasuresService,
              private assetRepresentationsService: AssetRepresentationsService,
              private mapIconsService: MapIconsService,
              private changeDetectorRef: ChangeDetectorRef,
              private messageService: MessageService) {
    this.initTimeOptionsAndRangeDates();
    this.triggersByStatus = {alarmTriggers: [], warningTriggers: [], inactivityTriggers: []};
    this.assetChangedStatus = new EventEmitter<string>();
  }

  private initTimeOptionsAndRangeDates(): void {
    const sevenDaysAgo: Date = new Date();
    const fourteenDaysAgo: Date = new Date();
    sevenDaysAgo.setHours(sevenDaysAgo.getHours() - 24 * 7);
    fourteenDaysAgo.setHours(fourteenDaysAgo.getHours() - 24 * 14);
    this.timeOptions = [
      {label: translate('misc.last7Days'), startDate: sevenDaysAgo},
      {label: translate('misc.last14Days'), startDate: fourteenDaysAgo}
    ];
    this.rangeDates = [sevenDaysAgo, new Date()];
  }

  private refreshAsset(): void {
    this.triggersCarousel = [];
    this.measurements = [];
    this.chartMeasures = [];

    this.loadedAsset = false;
    this.loadedAssetMeasurements = false;
    this.loadedAssetDeviceMeasurements = false;
    this.loadedAssetTriggers = false;

    this.selectedMeasurement = undefined;
    this.selectedStatus = undefined;
  }

  @Input()
  set assetUuid(assetUuid: string) {
    if (this.asset?.uuid !== assetUuid) {
      this.observerTriggers?.unsubscribe();
    }

    this.refreshAsset();

    this.assetsService.getAsset(assetUuid).toPromise()
      .then(response => {
        this.asset = response.asset;
        this.changeDetectorRef.detectChanges();

        this.status = this.hashedMapAsset.get(this.asset.uuid)?.status;
        this.statusColor = this.assetRepresentationsService.getColorFromLoadedPreferences(this.status) ?? this.mapIconsService.getNewDefaultIconMainColor();

        this.loadActivatedTriggersFromAsset(assetUuid);

        this.loadAssetMeasurementsOfAsset(assetUuid);

      })
      .finally(() => {
        this.loadedAsset = true;
        this.changeDetectorRef.detectChanges();
      });
  }

  private loadAssetMeasurementsOfAsset(assetUuid: string): void {
    this.assetsService.getAssetMeasurementsOfAsset(assetUuid).subscribe({
      next: (measurementsResponse) => {
        const measurements = [];
        const mappedMeasurements = measurementsResponse.assetMeasurements.map(measurement => {
          return {
            name: measurement.name,
            uuid: measurement.uuid,
            lastValue: measurement.lastValueReported,
            unit: measurement.measurementType.unit,
            type: 'ASSET'
          };
        });
        measurements.push(...mappedMeasurements);
        if (mappedMeasurements.length > 0) {
          this.selectedMeasurement = mappedMeasurements[0];
          this.changeDetectorRef.detectChanges();
          this.loadMeasures(mappedMeasurements[0]);
        }

        this.assetsService.getAssetDeviceMeasurements(assetUuid).subscribe({
          next: (deviceMeasurementsResponse) => {
            deviceMeasurementsResponse.deviceMeasurements.forEach(measurement => measurement.type = 'DEVICE');
            measurements.push(...deviceMeasurementsResponse.deviceMeasurements);
            this.measurements = [...measurements];

            if (this.selectedMeasurement === undefined && this.measurements.length > 0) {
              this.selectedMeasurement = this.measurements[0];
              this.changeDetectorRef.detectChanges();
              this.loadMeasures(this.measurements[0]);
            }
          },
          error: undefined,
          complete: () => {
            this.loadedAssetDeviceMeasurements = true;
            this.changeDetectorRef.detectChanges();
          }
        });
      },
      error: () => {
        this.loadedAssetDeviceMeasurements = true;
        this.changeDetectorRef.detectChanges();
      },
      complete: () => {
        this.loadedAssetMeasurements = true;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  private loadActivatedTriggersFromAsset(assetUuid: string): void {
    this.observerTriggers = this.assetsService.getActivatedAssetAndDeviceTriggers(assetUuid).subscribe({
        next: (triggerResponse: TriggersByStatus & { status: string }): void => {
          if (triggerResponse.status !== this.status) {
            this.hashedMapAsset.get(this.asset.uuid).status = triggerResponse.status;
            this.status = triggerResponse.status;
            this.statusColor = this.assetRepresentationsService.getColorFromLoadedPreferences(this.status) ?? this.mapIconsService.getNewDefaultIconMainColor();
            this.assetChangedStatus.emit(assetUuid);
            this.statusChangedMessage();
          }

          if (triggerResponse.status !== null) {
            this.triggersCarousel.push(...[{
              status: 'ALARM',
              count: triggerResponse.alarmTriggers.length
            }, {status: 'WARNING', count: triggerResponse.warningTriggers.length}, {
              status: 'INACTIVITY',
              count: triggerResponse.inactivityTriggers.length
            }]);
            this.triggersByStatus = triggerResponse;
            this.selectedStatusCategory = triggerResponse.status;
            this.setSelectedStatus(triggerResponse.status);
            this.changeDetectorRef.detectChanges();
          }
        },
        error: undefined,
        complete: () => {
          this.loadedAssetTriggers = true;
          this.changeDetectorRef.detectChanges();
        }
      }
    );
  }

  private loadMeasures(measurement: MapMeasurement): void {
    const utcRange = this.timezoneService.timestampRangeFromCalendarData(this.rangeDates);

    if (measurement.type === 'ASSET') {
      this.measuresService.searchAssetMeasures(this.asset.tenant.uuid, this.asset.uuid,
        measurement.uuid, utcRange.from, utcRange.until).toPromise().then(response => {
          if (response.measures.length > 0) {
            this.chartMeasures = [{
              name: measurement.name,
              data: this.measuresService.buildSensorMeasures(response.measures),
              tooltip: {valueSuffix: ' ' + (measurement.unit ?? '')}
            }];
            this.graphAxis = {
              labels: {
                format: '{value} ' + (measurement.unit ?? '')
              },
              title: {
                enabled: false
              }
            };
          } else {
            this.chartMeasures = [];
          }
        });
    } else {
      this.measuresService.searchDeviceMeasures(this.asset.tenant.uuid, measurement.deviceUuid,
        measurement.uuid, utcRange.from, utcRange.until).toPromise().then(response => {
          if (response.measures.length > 0) {
            this.chartMeasures = [{
              name: measurement.name,
              data: this.measuresService.buildSensorMeasures(response.measures),
              tooltip: {valueSuffix: ' ' + (measurement.unit ?? '')}
            }];
            this.graphAxis = {
              labels: {
                format: '{value} ' + (measurement.unit ?? '')
              },
              title: {
                enabled: false
              }
            };
          } else {
            this.chartMeasures = [];
          }
        });
    }
  }

  loadMeasuresIfChanged(measurement: MapMeasurement): void {
    if (this.selectedMeasurement !== measurement) {
      this.loadMeasures(measurement);
    }
  }

  dateChanged(event: any): void {
    this.rangeDates = [event.value.startDate, new Date()];
    this.loadMeasures(this.selectedMeasurement);
  }

  setSelectedStatus(status: string): void {
    this.selectedStatusCategory = status;

    if (status === 'ALARM') {
      this.selectedStatus = this.triggersByStatus.alarmTriggers;
    } else if (status === 'WARNING') {
      this.selectedStatus = this.triggersByStatus.warningTriggers;
    } else if (status === 'INACTIVITY') {
      this.selectedStatus = this.triggersByStatus.inactivityTriggers;
    }
  }

  private statusChangedMessage(): void{
    const statusTranslated: string = translate((('misc.representation.' + (this.status?.toLowerCase() ?? 'default'))));
    const summary = translate('warning.map.statusChanged');
    const detail = translate('warning.map.statusChangedDetails', {alias: this.asset.alias, newStatus: statusTranslated});
    this.messageService.add({severity: 'warn', summary, detail, sticky: true});
  }
}
